// frontend/src/components/QuestionComponent.tsx
import React from 'react';

interface Question {
  question_id: string;
  question_type: string;
  question_text: string;
  code_snippet?: string;
  options?: string;
  points: number;
  order_index: number;
  skill_focus?: string;
}

interface QuestionComponentProps {
  question: Question;
  value: string;
  onChange: (answer: string) => void;
  disabled?: boolean;
}

const QuestionComponent: React.FC<QuestionComponentProps> = ({ question, value, onChange, disabled = false }) => {
  const getSkillFocusIcon = (skillFocus?: string) => {
    switch (skillFocus) {
      case 'efficiency': return '⚡';
      case 'correctness': return '✅';
      case 'security': return '🔒';
      case 'ai-automation': return '🤖';
      default: return '💻';
    }
  };

  const formatQuestionText = (text: string) => {
    // Split by double newlines for paragraphs
    const paragraphs = text.split(/\n\s*\n/);

    return paragraphs.map((paragraph, index) => {
      // Handle bullet points and lists
      if (paragraph.includes('- ') || paragraph.includes('* ')) {
        const lines = paragraph.split('\n').filter(line => line.trim());
        const listItems = lines.filter(line => line.trim().startsWith('- ') || line.trim().startsWith('* '));
        const nonListLines = lines.filter(line => !line.trim().startsWith('- ') && !line.trim().startsWith('* '));

        return (
          <div key={index} className="mb-4">
            {nonListLines.length > 0 && (
              <div className="mb-2">
                {nonListLines.map((line, lineIndex) => (
                  <div key={lineIndex} className="mb-1">{line.trim()}</div>
                ))}
              </div>
            )}
            {listItems.length > 0 && (
              <ul className="list-disc list-inside space-y-1 ml-4">
                {listItems.map((item, itemIndex) => (
                  <li key={itemIndex} className="text-gray-200">
                    {item.replace(/^[-*]\s*/, '').trim()}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      }

      // Handle regular paragraphs with line breaks
      const lines = paragraph.split('\n').filter(line => line.trim());
      return (
        <div key={index} className="mb-4">
          {lines.map((line, lineIndex) => (
            <div key={lineIndex} className="mb-1">
              {line.trim()}
            </div>
          ))}
        </div>
      );
    });
  };

  const getSkillFocusColor = (skillFocus?: string) => {
    switch (skillFocus) {
      case 'efficiency': return 'bg-yellow-100 text-yellow-800';
      case 'correctness': return 'bg-green-100 text-green-800';
      case 'security': return 'bg-red-100 text-red-800';
      case 'ai-automation': return 'bg-purple-100 text-purple-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const renderMultipleChoice = () => {
    if (!question.options) return null;
    
    let options: string[] = [];
    try {
      options = JSON.parse(question.options);
    } catch (e) {
      console.error('Failed to parse options:', e);
      return <div className="text-red-400">Error loading options</div>;
    }

    return (
      <div className="space-y-3">
        {options.map((option, index) => (
          <label
            key={index}
            className={`flex items-start space-x-3 p-3 rounded-lg border border-gray-600 transition-colors duration-200 ${
              disabled
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:border-gray-500 cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name={`question-${question.question_id}`}
              value={option}
              checked={value === option}
              onChange={(e) => onChange(e.target.value)}
              disabled={disabled}
              className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <span className="text-white flex-1">{option}</span>
          </label>
        ))}
      </div>
    );
  };

  const renderFillInBlank = () => {
    return (
      <div className="space-y-4">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Enter your answer here..."
          className="w-full h-32 p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <div className="text-sm text-gray-400">
          Provide a concise answer. Be specific and include relevant details.
        </div>
      </div>
    );
  };

  const renderCodeReview = () => {
    return (
      <div className="space-y-4">
        {question.code_snippet && (
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <div className="text-sm text-gray-400 mb-2">Code to Review:</div>
            <pre className="text-green-400 text-sm">
              <code>{question.code_snippet}</code>
            </pre>
          </div>
        )}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Identify issues, suggest improvements, and explain your reasoning..."
          className="w-full h-40 p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <div className="text-sm text-gray-400">
          <strong>Consider:</strong> Security vulnerabilities, performance issues, code quality, best practices, and maintainability.
        </div>
      </div>
    );
  };

  const renderFreeform = () => {
    const isAIAutomation = question.skill_focus === 'ai-automation';

    return (
      <div className="space-y-4">
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={
            isAIAutomation
              ? "Describe your approach, tools you'd use, and key considerations..."
              : "Provide a detailed explanation with examples where appropriate..."
          }
          className="w-full h-48 p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <div className="text-sm text-gray-400">
          {isAIAutomation ? (
            <>
              <strong>AI Automation Focus:</strong> Consider prompt engineering, tool integration,
              code review practices, and human-AI collaboration strategies.
            </>
          ) : (
            <>
              <strong>Tip:</strong> Structure your answer clearly, provide examples,
              and explain your reasoning step by step.
            </>
          )}
        </div>
      </div>
    );
  };

  const renderCodingChallenge = () => {
    return (
      <div className="space-y-4">
        {question.code_snippet && (
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <div className="text-sm text-gray-400 mb-2">Starter Code / Context:</div>
            <pre className="text-green-400 text-sm">
              <code>{question.code_snippet}</code>
            </pre>
          </div>
        )}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Write your solution here. Include class/method definitions, implementation details, and any necessary imports..."
          className="w-full h-64 p-4 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        />
        <div className="text-sm text-gray-400">
          <strong>Coding Challenge:</strong> Implement a complete solution. Consider edge cases,
          time/space complexity, and clean, readable code. You may use any programming language.
        </div>
      </div>
    );
  };

  const renderQuestionContent = () => {
    switch (question.question_type) {
      case 'multiple_choice':
        return renderMultipleChoice();
      case 'fill_blank':
        return renderFillInBlank();
      case 'code_review':
        return renderCodeReview();
      case 'freeform':
        return renderFreeform();
      case 'coding_challenge':
        return renderCodingChallenge();
      default:
        return (
          <div className="text-red-400">
            Unsupported question type: {question.question_type}
          </div>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Question Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-3">
            {question.skill_focus && (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSkillFocusColor(
                  question.skill_focus
                )}`}
              >
                {getSkillFocusIcon(question.skill_focus)} {question.skill_focus}
              </span>
            )}
            <span className="text-sm text-gray-400">
              {question.points} point{question.points !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="text-xl font-semibold text-white leading-relaxed">
            {formatQuestionText(question.question_text)}
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="mt-6">
        {renderQuestionContent()}
      </div>

      {/* Question Type Info */}
      <div className="flex items-center justify-between text-sm text-gray-400 pt-4 border-t border-gray-700">
        <div className="flex items-center space-x-4">
          <span>
            Type: <span className="capitalize">{question.question_type.replace('_', ' ')}</span>
          </span>
          {question.skill_focus && (
            <span>
              Focus: <span className="capitalize">{question.skill_focus.replace('-', ' ')}</span>
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500">
          Question {question.order_index + 1}
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center space-x-2 text-xs text-gray-500">
        <div className={`w-2 h-2 rounded-full ${value ? 'bg-green-500' : 'bg-gray-600'}`}></div>
        <span>{value ? 'Answered' : 'Not answered'}</span>
      </div>
    </div>
  );
};

export default QuestionComponent;
